/**
 * AdminEmailAudit — /admin/email-audit
 *
 * Operator brief (Feb 18, 2026 — post production email loop hotfix):
 *   "Build /admin/email-audit as a small read-only operator
 *    confidence view. Show whether emails are being sent or
 *    blocked, grouped by purpose × reason × decision × hour.
 *    Keep it read-only. No resend buttons. No flashy charts."
 *
 * Doctrine:
 *   • Read-only. Zero mutations. Zero email-sending controls.
 *   • Calm slate palette — no green/red panic surfaces. SENT is
 *     a quiet emerald chip; BLOCKED is a muted amber chip;
 *     provider_error is slate-rose.
 *   • Single source of truth: /api/admin/email-audit.
 *   • Empty state explicitly affirmed: "No email safety decisions
 *     found yet." — not an error, just an honest blank.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  ShieldCheck, ShieldAlert, ShieldX, RefreshCw, Inbox, Globe,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;


function decisionChipClass(decision) {
  switch (decision) {
    case "sent":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "blocked":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "provider_error":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-slate-50 text-slate-600 border-slate-200";
  }
}


function DecisionIcon({ decision, className }) {
  if (decision === "sent")  return <ShieldCheck className={className} />;
  if (decision === "blocked") return <ShieldAlert className={className} />;
  if (decision === "provider_error") return <ShieldX className={className} />;
  return <Inbox className={className} />;
}


function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}


function GroupCard({ title, entries, testid }) {
  const items = Object.entries(entries || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-slate-200 bg-white p-5"
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-3">
        {title}
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-400">No data yet</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map(([key, count]) => (
            <li
              key={key}
              className="flex items-center justify-between gap-3 text-sm"
              data-testid={`${testid}-row-${key}`}
            >
              <span className="text-slate-700 truncate">{key}</span>
              <span className="text-slate-900 font-bold tabular-nums">{count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


export default function AdminEmailAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/api/admin/email-audit?limit=200`, {
        withCredentials: true,
      });
      setData(data);
    } catch (e) {
      setError(
        e?.response?.data?.detail
          || e?.message
          || "Failed to load email audit.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const env = data?.environment;
  const events = data?.events || [];
  const groups = data?.groups || {};
  const totals = groups.by_decision || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-email-audit-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h1
              className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight"
              data-testid="email-audit-title"
            >
              Email safety audit
            </h1>
            <p
              className="text-sm text-slate-600 mt-1 max-w-2xl"
              data-testid="email-audit-subtitle"
            >
              This view shows recent email delivery safety decisions
              for operational monitoring. Read-only — no actions,
              no resends, no user-facing controls.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchAudit}
            disabled={loading}
            data-testid="email-audit-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:border-slate-300 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Environment banner */}
        {env ? (
          <div
            data-testid="email-audit-env-banner"
            className={`rounded-xl border px-4 py-3 mb-6 flex items-center gap-3 text-sm ${
              env.is_prod
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-amber-200 bg-amber-50 text-amber-900"
            }`}
          >
            <Globe className="w-4 h-4 flex-none" />
            <div className="flex-1">
              <span className="font-bold">
                {env.is_prod ? "Production environment" : "Non-production environment"}
              </span>
              <span className="text-slate-600 ml-2">host: {env.host || "—"}</span>
            </div>
            <div className="flex gap-2">
              {env.kill_switch ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-900 text-white">
                  Kill switch ON
                </span>
              ) : null}
              {env.real_delivery_optin ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-200 text-slate-800">
                  Real-delivery opt-in
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Top totals row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {["sent", "blocked", "provider_error", "total"].map((k) => {
            const value = k === "total"
              ? (data?.total ?? 0)
              : (totals[k] || 0);
            const label = (
              k === "total" ? "Tracked"
              : k === "provider_error" ? "Provider error"
              : k.charAt(0).toUpperCase() + k.slice(1)
            );
            return (
              <div
                key={k}
                data-testid={`email-audit-total-${k}`}
                className={`rounded-xl border bg-white p-4 ${decisionChipClass(k === "total" ? "" : k)}`}
              >
                <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">
                  {label}
                </div>
                <div className="text-2xl font-extrabold tabular-nums mt-1">
                  {value}
                </div>
              </div>
            );
          })}
        </div>

        {/* Grouped breakdowns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <GroupCard
            title="By purpose"
            entries={groups.by_purpose}
            testid="email-audit-group-purpose"
          />
          <GroupCard
            title="By reason"
            entries={groups.by_reason}
            testid="email-audit-group-reason"
          />
          <GroupCard
            title="By decision"
            entries={groups.by_decision}
            testid="email-audit-group-decision"
          />
        </div>

        {/* Hourly bucket strip */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 mb-6">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-3">
            By hour (recent first)
          </div>
          {Object.keys(groups.by_hour || {}).length === 0 ? (
            <div className="text-sm text-slate-400">No data yet</div>
          ) : (
            <div className="space-y-1.5" data-testid="email-audit-by-hour">
              {Object.entries(groups.by_hour).map(([hour, counts]) => (
                <div
                  key={hour}
                  data-testid={`email-audit-hour-${hour}`}
                  className="flex items-center justify-between gap-3 text-sm py-1 border-b border-slate-100 last:border-b-0"
                >
                  <span className="text-slate-700 font-mono text-[12px]">{hour}</span>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(counts).map(([d, c]) => (
                      <span
                        key={d}
                        className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${decisionChipClass(d)}`}
                      >
                        {d} · {c}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Events table */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Recent events
            </div>
            <div className="text-[11px] text-slate-500 tabular-nums">
              showing {events.length} · limit {data?.limit ?? 200}
            </div>
          </div>

          {error ? (
            <div
              data-testid="email-audit-error"
              className="px-5 py-6 text-sm text-rose-700 bg-rose-50"
            >
              {error}
            </div>
          ) : loading && !data ? (
            <div className="px-5 py-10 text-sm text-slate-500" data-testid="email-audit-loading">
              Loading…
            </div>
          ) : events.length === 0 ? (
            <div
              data-testid="email-audit-empty-state"
              className="px-5 py-10 text-center"
            >
              <Inbox className="w-8 h-8 text-slate-400 mx-auto mb-3" />
              <div className="text-sm font-semibold text-slate-700">
                No email safety decisions found yet.
              </div>
              <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                Decisions appear here as soon as the platform attempts
                to send (or block) an email. Read-only — this view never
                triggers a send.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="email-audit-events-table">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">When</th>
                    <th className="text-left px-4 py-2 font-semibold">Decision</th>
                    <th className="text-left px-4 py-2 font-semibold">Purpose</th>
                    <th className="text-left px-4 py-2 font-semibold">Recipient</th>
                    <th className="text-left px-4 py-2 font-semibold">Reason</th>
                    <th className="text-left px-4 py-2 font-semibold">Env</th>
                    <th className="text-left px-4 py-2 font-semibold">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr
                      key={ev.id}
                      data-testid={`email-audit-row-${ev.id}`}
                      className="border-t border-slate-100 hover:bg-slate-50/60"
                    >
                      <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                        {fmtWhen(ev.ts)}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${decisionChipClass(ev.decision)}`}
                        >
                          <DecisionIcon
                            decision={ev.decision}
                            className="w-3 h-3"
                          />
                          {ev.decision}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-slate-700">{ev.purpose}</td>
                      <td
                        className="px-4 py-2 text-slate-700 font-mono text-[12px]"
                        title={`hash:${ev.to_hash}`}
                      >
                        {ev.to_masked}
                      </td>
                      <td className="px-4 py-2 text-slate-600 text-[12px]">
                        {ev.reason}
                      </td>
                      <td className="px-4 py-2 text-slate-600 text-[12px]">
                        {ev.is_prod ? "prod" : "non-prod"}
                      </td>
                      <td className="px-4 py-2 text-slate-500 text-[12px]">
                        <div className="truncate max-w-[200px]" title={ev.user_agent || ""}>
                          {ev.client_ip || "—"}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[11px] text-slate-400 mt-6 text-center">
          Decisions persisted for 30 days with masked recipients only.
          No sensitive payload, no reset tokens, no API keys.
        </p>
      </div>
    </div>
  );
}
